import assetUtils, { Asset, getITokenAddress } from 'app-lib/assetUtils'
import {
  ChainId,
  getBlockExplorerAddressLink,
  isChainSupported,
} from 'bzx-common/src/config/appConfig'
import BorrowRequest from 'bzx-common/src/domain/borrow/BorrowRequest'
import IBorrowInfo from 'bzx-common/src/domain/borrow/IBorrowInfo'
import RepayLoanRequest from 'bzx-common/src/domain/borrow/RepayLoanRequest'
import ManageCollateralRequest from 'bzx-common/src/domain/ManageCollateralRequest'
import { IInterestRatesFromApi } from 'bzx-common/src/lib/apis/bzxApi'
import errorUtils from 'bzx-common/src/lib/errorUtils'
import loanUtils from 'bzx-common/src/lib/loanUtils'
import _ from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import RootStore from '../../stores/RootStore'
import Loan from './Loan'

type Action = 'collateral' | 'borrow' | 'repay' | 'autoSyncData'

type Request = BorrowRequest | ManageCollateralRequest | RepayLoanRequest

export interface IBorrowItem {
  asset: Asset
  availableLiquidity: BigNumber
  borrowInterestRate: BigNumber
  interestRate: BigNumber
  isLoading: boolean
  iTokenAddress: string
  iTokenExplorerUrl: string
  supplyApr: string
  totalBorrow: string
  totalSupply: string
}

interface IBorrowStoreProps {
  action: Action | null
  aprs: Record<string, IInterestRatesFromApi>
  autoSyncData: boolean
  borrowInfo: IBorrowInfo | null
  errorLoan: Error | null
  errorBorrow: Error | null
  errorTask: Error | null
  filteredLoans: Loan[]
  isBorrowCompleted: boolean
  isOpen: boolean
  liquidities: Record<string, string>
  loans: Loan[]
  loansPending: boolean
  loansChecked: boolean
  loansError: Error | null
  loanId: string | null
  pending: boolean
  request: Request | null
  searchQuery: string
  sortBy: 'asset' | 'interestRate' | 'availableLiquidity'
  sortOrder: 'asc' | 'desc'
}

export default class BorrowStore implements IBorrowStoreProps {
  action = null
  aprs: Record<string, IInterestRatesFromApi> = {}
  liquidities: Record<string, string> = {}
  autoSyncData = false
  borrowInfo: IBorrowInfo | null = null
  errorBorrow = null
  errorLoan = null
  errorTask = null
  isBorrowCompleted = false
  isOpen = false
  loanId = null
  loans: Loan[] = []
  loansPending = false
  loansChecked = false
  /** Error when fetching the list of user loans */
  loansError: Error | null = null
  pending = false
  request: Request | null = null
  rootStore: RootStore
  recentLiquidationsNumber = 0
  searchQuery = ''
  sortBy: 'interestRate' | 'availableLiquidity' | 'asset' = 'interestRate'
  sortOrder: 'asc' | 'desc' = 'asc'
  readonly daysNumberForLoanActionNotification = 2

  get error() {
    return this.errorBorrow || this.errorLoan || this.errorTask || this.loansError
  }

  get borrowItems(): IBorrowItem[] {
    const { chain, statsStore } = this.rootStore
    if (!chain) {
      return []
    }
    const { chainId } = chain.chainDetails
    const { currentChainStats } = statsStore
    return chain.borrowing.assets.map((asset) => {
      const borrowAsset = assetUtils.wrapNativeAsset(chainId, asset) // fix for Weth token
      const stat = currentChainStats.find((stat) => stat.tokenName === borrowAsset)
      const iTokenAddress = getITokenAddress(chainId, asset)
      return {
        asset,
        interestRate: new BigNumber(stat ? stat.borrowApr : 0),
        isLoading: false,
        iTokenAddress,
        availableLiquidity: new BigNumber(stat ? stat.availableLiquidity : NaN),
        borrowInterestRate: new BigNumber(stat ? stat.borrowApr : 0),
        iTokenExplorerUrl: getBlockExplorerAddressLink(chainId, iTokenAddress),
        supplyApr: stat ? stat.supplyApr : '0',
        totalBorrow: stat ? stat.totalBorrow : 'NaN',
        totalSupply: stat ? stat.totalSupply : 'NaN',
      }
    })
  }

  get filteredBorrowItems() {
    let filtered = this.borrowItems
    if (this.rootStore.borrowStore.searchQuery) {
      filtered = filtered.filter((item) =>
        item.asset.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    }
    return _.orderBy(
      filtered,
      [this.sortBy === 'asset' ? this.sortBy : (item) => +item[this.sortBy]],
      [this.sortOrder]
    )
  }

  get loan() {
    return this.loans.find((item) => this.loanId === item.loanId)
  }

  get filteredLoans() {
    let filtered = this.loans
    if (this.searchQuery) {
      filtered = this.loans.filter((loan: Loan) =>
        loan.loanAsset.toLowerCase().includes(this.searchQuery)
      )
    }
    return filtered
  }

  updateLoan(updatedLoan: Loan) {
    this.set(
      'loans',
      this.loans.map((loan) => {
        if (updatedLoan.loanId === loan.loanId) {
          return updatedLoan
        }
        return loan
      })
    )
  }

  removeLoan(loanId: string) {
    const loans = this.loans
    this.set(
      'loans',
      loans.reduce((arr, loan) => {
        if (loanId !== loan.loanId) {
          arr.push(loan)
        }
        return arr
      }, [] as Loan[])
    )
  }

  sortList(by: 'interestRate' | 'availableLiquidity' | 'asset') {
    if (this.sortBy === by) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortBy = by
    }
  }

  setLoanPendingTx(loanId: string, pendingTx = true) {
    this.set(
      'loans',
      this.loans.map((loan) => {
        if (loanId === loan.loanId) {
          loan.set('pendingTx', pendingTx)
        }
        return loan
      })
    )
  }

  onLoanConfirmed(request: Request) {
    try {
      this.setLoanPendingTx(request.loanId)

      if (request instanceof BorrowRequest) {
        this.borrow(request)
      } else if (request instanceof ManageCollateralRequest) {
        this.collateral(request)
      } else if (request instanceof RepayLoanRequest) {
        this.repay(request)
      }
      this.onClose()
    } catch (err) {
      errorUtils.decorateError(err, { title: 'No task found' })
      this.set('errorLoan', err)
      console.error(err)
    }
  }

  async borrow(request: BorrowRequest) {
    const { web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    if (!chainId) {
      return
    }
    let tx
    try {
      const txSteps = await loanService.borrow(chainId, request)
      tx = await transactionStore.run('borrow', txSteps)
      if (tx.status === 'success') {
        await this.syncLoans()
        this.rootStore.statsStore.refreshForCurrentChain()
      }
      return tx
    } catch (err) {
      console.error('Could not borrow', err)
    }
  }

  async collateral(request: ManageCollateralRequest) {
    const { fulcrumProvider, web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    const { loanId } = request
    this.setLoanPendingTx(loanId)
    if (!chainId) {
      return
    }
    let tx
    try {
      const txSteps = await loanService.collateral(chainId, request)
      tx = await transactionStore.run('borrow-collateral', txSteps)
      if (tx.status === 'success') {
        const loanData = await loanUtils.getLoanById(fulcrumProvider, loanId)
        if (loanData) {
          return this.updateLoan(new Loan(this.rootStore, loanData))
        }
        this.syncLoans()
      }
      return tx
    } catch (err) {
      console.error(err)
    }
  }

  async repay(request: RepayLoanRequest) {
    const { fulcrumProvider, web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    const { loanId } = request
    this.setLoanPendingTx(loanId)
    if (!chainId) {
      return
    }
    let tx
    try {
      const txSteps = await loanService.repay(chainId, request)
      tx = await transactionStore.run('repay', txSteps)
      if (tx.status === 'success') {
        const loanData = await loanUtils.getLoanById(fulcrumProvider, loanId)
        if (loanData) {
          return this.updateLoan(new Loan(this.rootStore, loanData))
        }
        this.syncLoans()
      }
      return tx
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Reset error state to null for any model that might have an error.
   * borrowStore.error computed value will be null as a result.
   */
  clearError() {
    this.errorBorrow = null
    this.errorTask = null
    this.loansError = null
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IBorrowStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IBorrowStoreProps>) {
    Object.assign(this, props)
  }

  clearData() {
    this.borrowInfo = null
    this.isBorrowCompleted = false
    this.loans = []
    this.onClose()
    this.loansChecked = false
  }

  // TODO: there shouldnt be UI component state mixed in stores
  onClose() {
    this.loanId = null
    this.request = null
    this.action = null
    this.isOpen = false
  }

  createRequest(action: Action) {
    const loan = this.loan
    if (!loan) {
      return
    }
    switch (action) {
      case 'borrow':
        return new BorrowRequest(
          loan.loanId,
          loan.loanAsset,
          new BigNumber(0),
          loan.collateralAsset,
          new BigNumber(0)
        )
      case `collateral`: {
        return new ManageCollateralRequest(
          loan.loanId,
          loan.collateralAsset,
          loan.collateralAmount,
          false,
          loan.loanAsset
        )
      }
      case `repay`: {
        return new RepayLoanRequest(
          loan.loanAsset,
          loan.collateralAsset,
          loan.accountAddress,
          loan.loanId,
          new BigNumber(0),
          new BigNumber(0),
          loan.amountOwed
        )
      }
    }
  }

  /**
   * Loan list, all loan related data.
   */
  async syncLoans() {
    try {
      const loans = (await loanUtils.getLoansList(this.rootStore.fulcrumProvider)).map((loan) => {
        return new Loan(this.rootStore, loan)
      })
      this.assign({ loans })
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Could not list your loans' })
      this.assign({ loansError: err })
    } finally {
      this.assign({ loansPending: false, loansChecked: true })
    }
  }

  /**
   * Refresh all assets interest rates, liquidity, loans, all user related data
   */
  async syncData(chainId: ChainId, walletAddress: string) {
    this.assign({ pending: true, loansPending: !!walletAddress })
    await this.rootStore.statsStore.stats.fetch(chainId)
    if (walletAddress) {
      await this.syncLoans()
    }
    this.assign({ pending: false })
  }

  autoSync(enabled: boolean) {
    this.autoSyncData = enabled
  }

  init() {
    mobx.reaction(
      () => this.rootStore.transactionStore.isFinished,
      (isFinished) => {
        if (isFinished) {
          this.loans.forEach((loan) => loan.set('pendingTx', false))
        }
      }
    )

    mobx.reaction(
      () => this.action,
      (action) => {
        if (!action) {
          return
        }
        const request = this.createRequest(action)
        if (!request) {
          return
        }
        this.set('request', request)
        this.set('isOpen', true)
      }
    )

    mobx.autorun(() => {
      const { chainId, walletAddress } = this.rootStore.web3Connection
      if (this.autoSyncData) {
        if (chainId && isChainSupported(chainId)) {
          this.syncData(chainId, walletAddress)
        }
      }
    })
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
    this.init()
  }
}
